/**
 * 74.2 follow-up — the polar dawn (lane I, after 74.11 (c); presentation only, no version): the sky's orientation about the
 * planet's spin axis, with an obliquity by kind, bounded.
 *
 * Before: the sky's frame put the system's body→sun direction onto the scene's fixed light (40, 50, 30 — 45° up in the world)
 * and the spin axis (core/spinAxis.ts, seeded within 29° of +y) was seeded apart from it, so the sun's declination — its
 * angle off the axis's equator — came out anywhere from 16° to 74° (a uniform draw would reach 90°), and a battle's own
 * place saw a polar day in ten of the sixteen Story worlds: the sun never rose at four of them and never set at three, and
 * circled low at the rest (the "polar dawn" lane E and the weather sheet met). Now the orbital plane's normal stands
 * `obliquity` off the spin axis — a kind's bounded range, drawn per world — and the sun at the match's start lies in that
 * plane at a seeded season, so its declination is at most the obliquity: every place nearer the equator than
 * 90° − obliquity has a sunrise and a sunset each day, and a place at the pole has the polar day its climate already says.
 *
 * Nothing in the sim reads this: `matchHour` (core/skyClock.ts) reads the clock alone (tests/obliquity.test.ts holds it to
 * the closed form and scans the sim's modules for the import); the spin axis itself — the wind's bands, the climate's
 * latitude — is untouched. Pure: dmath and the seeded random, no three; the sky player (rendering/systemSkyPlay.ts) builds
 * its frame from the two directions.
 */
import type { Vec3 } from '../types';
import type { BodyKind } from './systemGen';
import { SeededRandom } from './random';
import { dsin, dcos, dasin } from './dmath';

const DEG = Math.PI / 180;

/**
 * The obliquity's range by the body's kind (rad). Rocky worlds 0–30° (Mercury's 0, Earth's 23, Mars's 25); gas giants
 * 0–28° (Jupiter's 3, Saturn's 27); ice giants and icy bodies 8–30°. Bounded on purpose: a Uranus lies on its side (98°)
 * and gives the polar days this takes away; a moon (a rocky body with a parent) takes the rocky range as its own.
 */
export const OBLIQUITY: Record<BodyKind, [number, number]> = {
  rocky: [0, 30 * DEG], gasGiant: [0, 28 * DEG], iceGiant: [8 * DEG, 30 * DEG], icy: [8 * DEG, 30 * DEG],
};

/** The sky's orientation for a world: the orbital plane's normal and the sun's direction at the match's start, in the world's frame. */
export interface SkyOrientation {
  /** the orbital plane's normal: `obliquity` off the spin axis */
  normal: Vec3;
  /** the body→sun direction at the start: in the plane, at the season */
  sun: Vec3;
  /** the angle between the axis and the plane's normal (rad) */
  obliquity: number;
  /** where in the year the match starts (rad): 0 the solstice that tilts the axis's own hemisphere sunward, π the other */
  season: number;
}

const dot = (a: Vec3, b: Vec3): number => a.x * b.x + a.y * b.y + a.z * b.z;
const cross = (a: Vec3, b: Vec3): Vec3 => ({ x: a.y * b.z - a.z * b.y, y: a.z * b.x - a.x * b.z, z: a.x * b.y - a.y * b.x });
const mix = (a: Vec3, ka: number, b: Vec3, kb: number): Vec3 => ({ x: a.x * ka + b.x * kb, y: a.y * ka + b.y * kb, z: a.z * ka + b.z * kb });
function unit(v: Vec3): Vec3 {
  const l = dot(v, v);
  if (l < 1e-18) return { x: 0, y: 1, z: 0 };
  const k = 1 / Math.sqrt(l);   // (Math.sqrt is correctly rounded in every engine — not on dmath's list)
  return { x: v.x * k, y: v.y * k, z: v.z * k };
}
/** Two unit directions across `a` (a right-handed frame with it). */
function across(a: Vec3): [Vec3, Vec3] {
  const t: Vec3 = Math.abs(a.x) < 0.9 ? { x: 1, y: 0, z: 0 } : { x: 0, y: 1, z: 0 };
  const e1 = unit(cross(a, t));
  return [e1, cross(a, e1)];
}

/**
 * The orientation for a world (`seed`, the body the sky found for it): the plane's normal tilted `obliquity` off the spin
 * axis about a seeded direction across it; the sun in the plane at a seeded season. Deterministic per seed and body.
 */
export function skyOrientation(seed: string, axis: Vec3, kind: BodyKind, bodyId: number): SkyOrientation {
  const a = unit(axis);
  const rng = new SeededRandom(`${seed}-obliquity-${bodyId}`);
  const [lo, hi] = OBLIQUITY[kind];
  const obliquity = rng.range(lo, hi), azimuth = rng.range(0, 2 * Math.PI), season = rng.range(0, 2 * Math.PI);
  const [e1, e2] = across(a);
  const m = mix(e1, dcos(azimuth), e2, dsin(azimuth));          // the direction the axis tilts toward, across it
  const normal = unit(mix(a, dcos(obliquity), m, dsin(obliquity)));
  // in the plane: u1 along the axis's own tilt (its projection onto the plane), u2 a quarter-year on
  const u1 = obliquity < 1e-6 ? e1 : unit(mix(a, 1, normal, -dot(a, normal)));
  const u2 = cross(normal, u1);
  const sun = unit(mix(u1, dcos(season), u2, dsin(season)));
  return { normal, sun, obliquity, season };
}

/** The sun's declination (rad): its angle off the spin axis's equator — at most the obliquity, by construction. */
export function declinationOf(axis: Vec3, sun: Vec3): number {
  return dasin(Math.max(-1, Math.min(1, dot(unit(axis), unit(sun)))));
}

/** A place's day under a declination: the sun's elevation (rad) at its noon and at its midnight — a sunrise and a sunset when the first is over the horizon and the second under it. */
export function dayAt(latitude: number, declination: number): { noon: number; midnight: number } {
  return { noon: Math.PI / 2 - Math.abs(latitude - declination), midnight: Math.abs(latitude + declination) - Math.PI / 2 };
}
